import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
import {Button, Card, Divider, IconButton} from "@mui/material";
import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import {RhfTextField} from "mui-rhf-integration";
import {type UseFormReturn, useFieldArray} from "react-hook-form";
import {FREE_FORM, VALUE_LIST, WAIVER} from "@/components/Fees/WaiverQuestion.tsx";
import type {
    FeeProposalFormValues,
    FeeProposalRequestSchemaType,
    FormQuestion,
} from "@/components/Forms/FeeProposalRequestForm.tsx";

type Props = {
    form: UseFormReturn<FeeProposalFormValues, any, FeeProposalRequestSchemaType>;
};

const FeeQuestionsForm = ({form}: Props) => {
    const {control, watch, setValue} = form;
    const {fields, append, remove} = useFieldArray({control, name: "questions"});
    const watchFieldArray = watch("questions");
    const controlledFields = fields.map((field, index) => {
        return {
            ...field,
            ...watchFieldArray[index],
        };
    });

    return (
        <Grid container>
            {controlledFields.map((question: FormQuestion, index: number) => (
                <Card key={`question-${question.id}`} sx={{width: "100%", p: 1, mb: 2}}>
                    <Grid container>
                        <Grid size={{sm: 2, xs: 12}} sx={{p: 1}}>
                            <RhfTextField
                                select
                                control={form.control}
                                name={`questions.${index}.sortOrder`}
                                label="Sort Order"
                                sx={{
                                    width: "100%",
                                }}
                            >
                                {[...Array(controlledFields.length).keys()].map(sort => (
                                    <MenuItem
                                        key={`questions.${index}.sortOrder[${sort}]`}
                                        value={`${sort + 1}`}
                                    >
                                        {sort + 1}
                                    </MenuItem>
                                ))}
                            </RhfTextField>
                        </Grid>
                        <Grid size={{sm: 10, xs: 12}} sx={{p: 1}}>
                            <RhfTextField
                                control={form.control}
                                name={`questions.${index}.question`}
                                label={"Question"}
                                sx={{
                                    width: "100%",
                                }}
                                multiline
                                maxRows={6}
                            />
                        </Grid>
                        <Grid
                            size={{
                                xs: 12,
                                md: 3,
                            }}
                            sx={{p: 1}}
                        >
                            <RhfTextField
                                select
                                control={form.control}
                                name={`questions.${index}.responseType`}
                                label="Response Type"
                                sx={{
                                    width: "100%",
                                }}
                            >
                                <MenuItem value={VALUE_LIST}>Value List</MenuItem>
                                <MenuItem value={FREE_FORM}>Free Form</MenuItem>
                                <MenuItem value={WAIVER}>Waiver</MenuItem>
                            </RhfTextField>
                        </Grid>
                        <Grid
                            size={{
                                xs: 12,
                                md: 9,
                            }}
                            sx={{
                                p: 1,
                                display: "flex",
                                flexDirection: "row",
                                justifyContent: {xs: "center", md: "flex-start"},
                            }}
                        >
                            {question.responseType === VALUE_LIST && (
                                <RhfTextField
                                    control={form.control}
                                    name={`questions.${index}.answerList`}
                                    label={"Value List Options"}
                                    placeholder={"L,M,S"}
                                    sx={{
                                        width: "100%",
                                    }}
                                />
                            )}
                            <IconButton
                                edge="end"
                                aria-label="delete"
                                onClick={() => {
                                    const currentSortIndex = Number(
                                        watchFieldArray[index].sortOrder,
                                    );
                                    if (!isNaN(currentSortIndex)) {
                                        for (const idx of watchFieldArray.keys()) {
                                            if (idx === index) {
                                                continue;
                                            }
                                            const otherSort = Number(
                                                watchFieldArray[idx].sortOrder,
                                            );
                                            if (
                                                !isNaN(otherSort) &&
                                                otherSort >= currentSortIndex &&
                                                otherSort > 1
                                            ) {
                                                setValue(
                                                    `questions.${idx}.sortOrder`,
                                                    `${otherSort - 1}`,
                                                );
                                            }
                                        }
                                    }
                                    remove(index);
                                }}
                            >
                                <RemoveCircleIcon />
                            </IconButton>
                        </Grid>
                        <Divider />
                    </Grid>
                </Card>
            ))}
            <Grid size={12}>
                <Button
                    variant="outlined"
                    onClick={() => {
                        append({
                            question: "",
                            responseType: "",
                            answerList: "",
                            sortOrder: `${controlledFields.length + 1}`,
                        });
                    }}
                    sx={{
                        mb: 2,
                        mr: 2,
                    }}
                >
                    Add Question
                </Button>
            </Grid>
        </Grid>
    );
};

export default FeeQuestionsForm;
